/**
 * EmailCapture — free Prnt Pack signup for blog readers.
 *
 * Flow: reader enters email → presses Send → a popup asks the child's age band
 * (4-7 / 8-12 / 13-16) → on choosing, we write the signup (with ageBand) and the
 * matching age-tailored Prnt Pack is delivered: an instant on-screen download
 * AND emailed (the onEmailSignup Cloud Function sends it). One pack, on signup —
 * no weekly/Monday cadence.
 *
 * Two modes:
 *   - 'inline'        : card within the article body / listing
 *   - 'sticky-footer' : fixed bottom slide-up after 30s OR 50% scroll depth
 *
 * The Firestore write is awaited (5s cap). If it fails or stalls (ad-blockers
 * commonly block firestore.googleapis.com) we fall back to an HTTPS Cloud
 * Function so the signup still lands. Success only shows when the signup is
 * actually saved — never a fake confirmation.
 */

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db, isFirebaseConfigured } from '../../firebase/config';
import { useLangStore } from '../../store/langStore';
import { Mascot } from '../mascots/Mascot';
import { trackEvent } from '../../lib/analytics';

const DISMISS_KEY = 'goodatoms_email_dismissed';
const DISMISS_DAYS = 7;
const SCROLL_THRESHOLD = 0.5;
const TIME_THRESHOLD_MS = 30_000;
const FALLBACK_TIMEOUT_MS = 5000;

type EmailCaptureSource = 'blog' | 'sticky' | 'undring-listing';
type AgeBand = '4-7' | '8-12' | '13-16';
const AGE_BANDS: AgeBand[] = ['4-7', '8-12', '13-16'];

export interface EmailCaptureProps {
  mode: 'inline' | 'sticky-footer';
  slug?: string;
  source: EmailCaptureSource;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Resolve the age + language matched Prnt Pack PDF (static, in public/prnt/). */
function packUrl(ageBand: AgeBand, lang: string): string {
  return `/prnt/wonder-pack-${ageBand}-${lang === 'en' ? 'en' : 'no'}.pdf`;
}

function isRecentlyDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return ageDays < DISMISS_DAYS;
  } catch {
    return false;
  }
}

function readStoredUtm(): unknown {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem('goodatoms_utm');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Server-side fallback for the direct Firestore write. Ad-blockers and privacy
 * extensions frequently block firestore.googleapis.com, silently dropping the
 * signup. When the direct write fails or stalls, we POST to an HTTPS Cloud
 * Function (a different domain, rarely blocked) that writes the signup with the
 * Admin SDK; the onEmailSignup trigger then sends the pack email as normal.
 * Returns true only if the signup was accepted.
 */
async function postSignupFallback(body: {
  email: string;
  lang: string;
  source: EmailCaptureSource;
  slug: string | null;
  ageBand: AgeBand;
  utm: unknown;
}): Promise<boolean> {
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined;
  if (!projectId) return false;
  const url = `https://europe-west1-${projectId}.cloudfunctions.net/httpEmailSignup`;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    });
    if (!res.ok) return false;
    const json = (await res.json().catch(() => null)) as { ok?: boolean } | null;
    return Boolean(json?.ok);
  } catch {
    return false;
  }
}

export function EmailCapture({ mode, slug, source }: EmailCaptureProps) {
  const { lang } = useLangStore();

  const [email, setEmail] = useState('');
  const [askingAge, setAskingAge] = useState(false);
  const [ageBand, setAgeBand] = useState<AgeBand | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visible, setVisible] = useState(mode === 'inline');
  const [dismissed, setDismissed] = useState(false);

  // Sticky-footer visibility — show after time OR scroll depth, unless dismissed.
  useEffect(() => {
    if (mode !== 'sticky-footer') return;
    // Recently dismissed → never schedule the reveal. Nothing renders while
    // `visible` stays false, so no state needs to change here.
    if (isRecentlyDismissed()) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let cleaned = false;

    const reveal = () => {
      if (cleaned) return;
      setVisible(true);
    };

    timer = setTimeout(reveal, TIME_THRESHOLD_MS);

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const progress = window.scrollY / scrollable;
      if (progress >= SCROLL_THRESHOLD) reveal();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      cleaned = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
  }, [mode]);

  const copy = useMemo(() => {
    if (lang === 'no') {
      return {
        heading: 'Få en gratis Prnt Pack',
        body: 'Morsomme eksperimenter og arbeidsark du kan skrive ut og gjøre på kjøkkenbenken. Ingen konto, ingen kort.',
        placeholder: 'din@epost.no',
        cta: 'Send meg Prnt Pack',
        sending: 'Sender…',
        invalid: 'Sjekk at e-postadressen er riktig.',
        ageTitle: 'Hvor gammelt er barnet?',
        ageHint: 'Så pakken passer akkurat for dem.',
        ageLabels: { '4-7': '4–7 år', '8-12': '8–12 år', '13-16': '13–16 år' } as Record<AgeBand, string>,
        successTitle: 'Sjekk innboksen din ✓',
        successBody: 'Prnt Pack er på vei til innboksen din.',
        download: 'Last ned Prnt Pack',
        failed: 'Det gikk ikke gjennom — prøv igjen om litt.',
        dismiss: 'Lukk',
      };
    }
    return {
      heading: 'Get a free Prnt Pack',
      body: 'Fun experiments + worksheets to print and do at the kitchen table. No account, no card needed.',
      placeholder: 'you@email.com',
      cta: 'Send me the Prnt Pack',
      sending: 'Sending…',
      invalid: 'Please check that your email address looks right.',
      ageTitle: 'How old is your child?',
      ageHint: 'So the pack is just right for them.',
      ageLabels: { '4-7': '4–7 years', '8-12': '8–12 years', '13-16': '13–16 years' } as Record<AgeBand, string>,
      successTitle: 'Check your inbox ✓',
      successBody: 'Your Prnt Pack is on its way to your inbox.',
      download: 'Download your Prnt Pack',
      failed: 'That didn’t go through — please try again in a moment.',
      dismiss: 'Dismiss',
    };
  }, [lang]);

  // Step 1 — validate the email, then open the age popup. No write yet.
  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (sending || submitted) return;
    if (!EMAIL_RE.test(email.trim())) {
      setError(copy.invalid);
      return;
    }
    setError(null);
    setAskingAge(true);
  };

  // Step 2 — age chosen: write the signup (with ageBand) and deliver the pack.
  const chooseAge = async (band: AgeBand) => {
    if (sending || submitted) return;
    setAgeBand(band);
    setAskingAge(false);
    setError(null);
    setSending(true);

    const trimmed = email.trim();

    // No Firebase (local demo/preview) — keep an optimistic success path.
    if (!isFirebaseConfigured() || !db) {
      if (import.meta.env.DEV) {
        console.warn('[EmailCapture] Firebase not configured — write skipped');
      }
      setSubmitted(true);
      setSending(false);
      trackEvent('email_capture', { source, slug });
      return;
    }

    const payload = {
      email: trimmed,
      lang,
      source,
      slug: slug ?? null,
      ageBand: band,
      ts: serverTimestamp(),
      utm: readStoredUtm(),
    };
    // Plain (JSON-safe) copy for the HTTPS fallback — no serverTimestamp sentinel.
    const fallbackBody = {
      email: trimmed,
      lang,
      source,
      slug: slug ?? null,
      ageBand: band,
      utm: readStoredUtm(),
    };

    // 1) Try the direct Firestore write, capped — a blocked/offline connection
    //    leaves addDoc pending forever, so race it and fall through on timeout.
    let written = false;
    try {
      const outcome = await Promise.race([
        addDoc(collection(db, 'email_signups'), payload).then(() => 'written' as const),
        new Promise<'timeout'>((resolve) =>
          setTimeout(() => resolve('timeout'), FALLBACK_TIMEOUT_MS)
        ),
      ]);
      written = outcome === 'written';
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn('[EmailCapture] direct write failed; trying server fallback', err);
      }
    }

    // 2) Direct write didn't confirm — POST to the un-blockable server endpoint.
    if (!written) {
      written = await postSignupFallback(fallbackBody);
    }

    if (written) {
      setSubmitted(true);
      trackEvent('email_capture', { source, slug });
    } else {
      setError(copy.failed);
      trackEvent('email_capture_error', { source, slug });
    }
    setSending(false);
  };

  const dismissSticky = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  const downloadUrl = ageBand ? packUrl(ageBand, lang) : null;

  const agePopup = (
    <AgePopup
      open={askingAge}
      copy={copy}
      onChoose={chooseAge}
      onCancel={() => setAskingAge(false)}
    />
  );

  // ----- INLINE MODE -----
  if (mode === 'inline') {
    return (
      <motion.section
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        className="max-w-3xl mx-auto my-12 p-6 md:p-8 rounded-2xl border border-teal/20"
        style={{ backgroundColor: '#F5EDD8' }}
      >
        <InlineContent
          submitted={submitted}
          sending={sending}
          error={error}
          email={email}
          setEmail={setEmail}
          onSubmit={onSubmit}
          copy={copy}
          downloadUrl={downloadUrl}
        />
        {agePopup}
      </motion.section>
    );
  }

  // ----- STICKY FOOTER MODE -----
  if (dismissed) return null;

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            key="sticky-email"
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 280, damping: 30 }}
            className="fixed bottom-0 inset-x-0 z-50 px-4 pb-4 pointer-events-none"
          >
            <div
              className="pointer-events-auto mx-auto w-full sm:max-w-md rounded-2xl shadow-2xl border border-teal/30 p-4 md:p-5"
              style={{ backgroundColor: '#FAFAF8' }}
              role="dialog"
              aria-label={copy.heading}
            >
              <button
                type="button"
                onClick={dismissSticky}
                aria-label={copy.dismiss}
                className="absolute top-2 right-3 text-ink-faint hover:text-ink-muted text-lg leading-none"
                style={{ position: 'relative', float: 'right', marginTop: '-4px' }}
              >
                ×
              </button>
              <InlineContent
                submitted={submitted}
                sending={sending}
                error={error}
                email={email}
                setEmail={setEmail}
                onSubmit={onSubmit}
                copy={copy}
                downloadUrl={downloadUrl}
                compact
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {agePopup}
    </>
  );
}

type Copy = {
  heading: string;
  body: string;
  placeholder: string;
  cta: string;
  sending: string;
  invalid: string;
  ageTitle: string;
  ageHint: string;
  ageLabels: Record<AgeBand, string>;
  successTitle: string;
  successBody: string;
  download: string;
  failed: string;
  dismiss: string;
};

interface InlineContentProps {
  submitted: boolean;
  sending: boolean;
  error: string | null;
  email: string;
  setEmail: (v: string) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  copy: Copy;
  downloadUrl: string | null;
  compact?: boolean;
}

function InlineContent({
  submitted,
  sending,
  error,
  email,
  setEmail,
  onSubmit,
  copy,
  downloadUrl,
  compact,
}: InlineContentProps) {
  if (submitted) {
    return (
      <div className="flex items-center gap-4">
        <div className="shrink-0">
          <Mascot subject="science" emotion="celebrating" size={compact ? 48 : 64} />
        </div>
        <div>
          <p
            className="font-display font-bold text-ink"
            style={{ fontSize: compact ? '1rem' : '1.125rem' }}
          >
            {copy.successTitle}
          </p>
          <p
            className="font-body text-ink-muted mb-3"
            style={{ fontSize: compact ? '0.875rem' : '1rem' }}
          >
            {copy.successBody}
          </p>
          {downloadUrl && (
            <a
              href={downloadUrl}
              download
              className="inline-block rounded-full px-5 py-2.5 text-white font-display font-semibold text-sm hover:bg-teal-dark transition-colors shadow-glow"
              style={{ backgroundColor: '#00C2A8' }}
            >
              ⬇ {copy.download}
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={compact ? '' : 'flex flex-col md:flex-row md:items-center gap-5'}>
      {!compact && (
        <div className="shrink-0 hidden md:block">
          <Mascot subject="arts" emotion="happy" size={72} />
        </div>
      )}
      <div className="flex-1">
        <h3
          className={`font-display font-bold text-ink ${compact ? 'text-base' : 'text-xl md:text-2xl'} mb-1`}
        >
          {copy.heading}
        </h3>
        <p
          className={`font-body text-ink-muted ${compact ? 'text-sm' : 'text-base'} mb-3`}
        >
          {copy.body}
        </p>
        <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2">
          <label className="sr-only" htmlFor={`email-capture-${compact ? 'sticky' : 'inline'}`}>
            {copy.placeholder}
          </label>
          <input
            id={`email-capture-${compact ? 'sticky' : 'inline'}`}
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={copy.placeholder}
            aria-invalid={error ? true : undefined}
            className="flex-1 min-w-0 rounded-full px-4 py-2.5 bg-white border border-gray-200 font-body text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-teal/50 focus:border-teal"
          />
          <button
            type="submit"
            disabled={sending}
            className="shrink-0 rounded-full px-5 py-2.5 bg-teal text-white font-display font-semibold text-sm hover:bg-teal-dark transition-colors shadow-glow disabled:opacity-60"
            style={{ backgroundColor: '#00C2A8' }}
          >
            {sending ? copy.sending : copy.cta}
          </button>
        </form>
        {error && (
          <p className="mt-2 text-sm font-body text-red-600" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

/** Age-band popup shown after the reader presses Send. */
function AgePopup({
  open,
  copy,
  onChoose,
  onCancel,
}: {
  open: boolean;
  copy: Copy;
  onChoose: (band: AgeBand) => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="age-popup"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(13, 27, 42, 0.55)' }}
          onClick={onCancel}
          role="dialog"
          aria-modal="true"
          aria-label={copy.ageTitle}
        >
          <motion.div
            initial={{ scale: 0.92, y: 12, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.92, y: 12, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 26 }}
            className="w-full max-w-sm rounded-2xl p-6 text-center shadow-2xl"
            style={{ backgroundColor: '#FAFAF8' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center mb-3">
              <Mascot subject="science" emotion="happy" size={56} />
            </div>
            <h4 className="font-display font-bold text-ink text-xl mb-1">{copy.ageTitle}</h4>
            <p className="font-body text-ink-muted text-sm mb-5">{copy.ageHint}</p>
            <div className="flex flex-col gap-2.5">
              {AGE_BANDS.map((band) => (
                <button
                  key={band}
                  type="button"
                  onClick={() => onChoose(band)}
                  className="w-full rounded-full px-5 py-3 font-display font-semibold text-ink border-2 border-teal/30 hover:border-teal hover:text-teal-dark transition-colors"
                  style={{ backgroundColor: '#FFFFFF' }}
                >
                  {copy.ageLabels[band]}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="mt-4 text-sm font-body text-ink-faint hover:text-ink-muted"
            >
              {copy.dismiss}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default EmailCapture;
